import React, { useState, useEffect } from 'react';
import {
  Plus, Trash2, Edit2, X, Loader2, CheckCircle, AlertCircle,
  Wifi, Key, Database, Globe, Server, Link, RefreshCw, Send, Newspaper,
} from 'lucide-react';
import {
  DataConnection, DataConnectionCreate, DataConnectionUpdate,
  ConnectionType, Direction,
} from '../types/connection';
import * as connSvc from '../services/connectionService';
import ConfirmationModal from '../components/ConfirmationModal';
import { formatDate } from '../utils';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const CONNECTION_TYPE_META: Record<ConnectionType, { label: string; icon: React.ReactNode; color: string }> = {
  exchange: {
    label: 'Exchange',
    icon: <Globe className="w-4 h-4" />,
    color: 'text-green-300 bg-green-400/10 border-green-400/30',
  },
  rest_api: {
    label: 'REST API',
    icon: <Server className="w-4 h-4" />,
    color: 'text-blue-300 bg-blue-400/10 border-blue-400/30',
  },
  websocket: {
    label: 'WebSocket',
    icon: <Wifi className="w-4 h-4" />,
    color: 'text-yellow-300 bg-yellow-400/10 border-yellow-400/30',
  },
  mcp_server: {
    label: 'MCP Server',
    icon: <Link className="w-4 h-4" />,
    color: 'text-purple-300 bg-purple-400/10 border-purple-400/30',
  },
  internal_db: {
    label: 'Internal DB',
    icon: <Database className="w-4 h-4" />,
    color: 'text-slate-300 bg-slate-400/10 border-slate-400/30',
  },
  telegram: {
    label: 'Telegram',
    icon: <Send className="w-4 h-4" />,
    color: 'text-sky-300 bg-sky-400/10 border-sky-400/30',
  },
  mattermost: {
    label: 'Mattermost',
    icon: <Send className="w-4 h-4" />,
    color: 'text-indigo-300 bg-indigo-400/10 border-indigo-400/30',
  },
  news_api: {
    label: 'News Feed',
    icon: <Newspaper className="w-4 h-4" />,
    color: 'text-amber-300 bg-amber-400/10 border-amber-400/30',
  },
};

const EXCHANGE_PROVIDERS = ['binance', 'coinbase', 'kraken', 'bybit', 'okx', 'alpaca', 'kucoin', 'bitfinex'];
const NEWS_PROVIDERS = ['combined', 'alpaca', 'finnhub'];

const DIRECTION_META: Record<Direction, { label: string; color: string }> = {
  in:   { label: 'IN',   color: 'text-cyan-300 bg-cyan-400/10' },
  out:  { label: 'OUT',  color: 'text-orange-300 bg-orange-400/10' },
  both: { label: 'BOTH', color: 'text-violet-300 bg-violet-400/10' },
};

const EMPTY_FORM: DataConnectionCreate = {
  name: '',
  connection_type: 'exchange',
  provider: 'binance',
  direction: 'in',
};

// ---------------------------------------------------------------------------
// ConnectionCard
// ---------------------------------------------------------------------------

interface ConnectionCardProps {
  conn: DataConnection;
  onEdit: (c: DataConnection) => void;
  onDelete: (c: DataConnection) => void;
  onTest: (c: DataConnection) => void;
  testing: boolean;
}

const ConnectionCard: React.FC<ConnectionCardProps> = ({ conn, onEdit, onDelete, onTest, testing }) => {
  const meta = CONNECTION_TYPE_META[conn.connection_type];
  const dirMeta = DIRECTION_META[conn.direction];

  return (
    <div className="bg-slate-800/60 backdrop-blur-md rounded-xl border border-slate-700 p-5 flex flex-col gap-3">
      {/* Header */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <div className={`p-2 rounded-lg border ${meta.color}`}>{meta.icon}</div>
          <div>
            <h3 className="font-semibold text-white text-sm">{conn.name}</h3>
            <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
              <span className={`px-1.5 py-0.5 rounded text-xs font-bold border ${meta.color}`}>
                {meta.label}
              </span>
              <span className={`px-1.5 py-0.5 rounded text-xs font-bold ${dirMeta.color}`}>
                {dirMeta.label}
              </span>
              {conn.provider && (
                <span className="px-1.5 py-0.5 rounded text-xs bg-slate-700 text-slate-300 font-mono">
                  {conn.provider}
                </span>
              )}
              {!conn.is_active && (
                <span className="px-1.5 py-0.5 rounded text-xs bg-red-400/10 text-red-400 border border-red-400/20">
                  disabled
                </span>
              )}
            </div>
          </div>
        </div>
        {conn.has_api_key && (
          <span title="Has API credentials"><Key className="w-3.5 h-3.5 text-yellow-400/70 flex-shrink-0 mt-1" /></span>
        )}
      </div>

      {/* Base URL */}
      {conn.base_url && (
        <p className="text-slate-500 text-xs font-mono truncate" title={conn.base_url}>
          {conn.base_url}
        </p>
      )}

      {/* Test status */}
      {conn.last_tested_at && (
        <div className="flex items-center gap-1.5 text-xs">
          {conn.last_test_success ? (
            <CheckCircle className="w-3.5 h-3.5 text-green-400" />
          ) : (
            <AlertCircle className="w-3.5 h-3.5 text-red-400" />
          )}
          <span className={conn.last_test_success ? 'text-green-400' : 'text-red-400'}>
            {conn.last_test_success ? 'Connected' : 'Failed'}
          </span>
          <span className="text-slate-600">·</span>
          <span className="text-slate-500">{formatDate(conn.last_tested_at)}</span>
          {!conn.last_test_success && conn.last_test_error && (
            <span className="text-red-400/70 truncate max-w-[200px]" title={conn.last_test_error}>
              {conn.last_test_error}
            </span>
          )}
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between pt-1 border-t border-slate-700/50">
        <span className="text-xs text-slate-600">{formatDate(conn.created_at)}</span>
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => onTest(conn)}
            disabled={testing}
            title="Test connection"
            className="px-2.5 py-1 rounded-md bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-300 text-xs flex items-center gap-1 disabled:opacity-40 transition"
          >
            {testing ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
            Test
          </button>
          <button
            onClick={() => onEdit(conn)}
            className="p-1.5 rounded-md hover:bg-slate-700 text-slate-400 hover:text-white transition"
            title="Edit"
          >
            <Edit2 className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={() => onDelete(conn)}
            className="p-1.5 rounded-md hover:bg-red-500/20 text-slate-400 hover:text-red-400 transition"
            title="Delete"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Test result banner
// ---------------------------------------------------------------------------

interface TestBannerProps {
  success: boolean;
  message: string;
  onClose: () => void;
}

const TestBanner: React.FC<TestBannerProps> = ({ success, message, onClose }) => (
  <div
    className={`fixed bottom-6 right-6 z-50 flex items-start gap-3 rounded-xl border p-4 shadow-xl max-w-sm ${
      success
        ? 'bg-green-900/80 border-green-500/40 text-green-200'
        : 'bg-red-900/80 border-red-500/40 text-red-200'
    }`}
  >
    {success ? <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" /> : <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />}
    <div className="flex-1">
      <p className="font-semibold text-sm">{success ? 'Connection successful' : 'Connection failed'}</p>
      <p className="text-xs mt-0.5 opacity-80">{message}</p>
    </div>
    <button onClick={onClose} className="opacity-60 hover:opacity-100 transition">
      <X className="w-4 h-4" />
    </button>
  </div>
);

// ---------------------------------------------------------------------------
// Create/Edit Modal
// ---------------------------------------------------------------------------

interface ConnectionModalProps {
  editing: DataConnection | null;
  onClose: () => void;
  onSave: (payload: DataConnectionCreate | DataConnectionUpdate) => Promise<void>;
  saving: boolean;
  saveError: string | null;
}

const ConnectionModal: React.FC<ConnectionModalProps> = ({ editing, onClose, onSave, saving, saveError }) => {
  const [form, setForm] = useState<DataConnectionCreate>(
    editing
      ? {
          name: editing.name,
          connection_type: editing.connection_type,
          provider: editing.provider || '',
          base_url: editing.base_url || '',
          direction: editing.direction,
          config: editing.config || undefined,
        }
      : { ...EMPTY_FORM },
  );
  const [configText, setConfigText] = useState(
    editing?.config ? JSON.stringify(editing.config, null, 2) : '',
  );
  const [configError, setConfigError] = useState('');

  const isExchange = form.connection_type === 'exchange';
  const isRest = form.connection_type === 'rest_api';
  const isMcp = form.connection_type === 'mcp_server';
  const isInternalDb = form.connection_type === 'internal_db';
  const isTelegram = form.connection_type === 'telegram';
  const isMattermost = form.connection_type === 'mattermost';
  const isNews = form.connection_type === 'news_api';
  const needsCredentials = isExchange || isRest || isMcp || isTelegram || isMattermost || isNews;

  const handleSubmit = async () => {
    let parsedConfig: Record<string, unknown> | undefined;
    if (configText.trim()) {
      try {
        parsedConfig = JSON.parse(configText);
        setConfigError('');
      } catch {
        setConfigError('Invalid JSON');
        return;
      }
    }
    await onSave({ ...form, config: parsedConfig });
  };

  const set = (field: keyof DataConnectionCreate, value: unknown) =>
    setForm(f => ({ ...f, [field]: value }));

  const inputCls = 'w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-500';

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-slate-800 rounded-xl border border-slate-700 w-full max-w-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-slate-700">
          <h2 className="text-xl font-bold text-white">{editing ? 'Edit Connection' : 'New Connection'}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-6 space-y-4">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1">Name <span className="text-red-400">*</span></label>
            <input
              value={form.name}
              onChange={e => set('name', e.target.value)}
              placeholder="e.g. Binance Live Feed"
              className={inputCls}
            />
          </div>

          {/* Type + Direction */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">Type</label>
              <select
                value={form.connection_type}
                onChange={e => set('connection_type', e.target.value as ConnectionType)}
                className={inputCls}
              >
                <option value="exchange">Exchange (CCXT)</option>
                <option value="news_api">News Feed</option>
                <option value="rest_api">REST API</option>
                <option value="websocket">WebSocket</option>
                <option value="mcp_server">MCP Server</option>
                <option value="internal_db">Internal DB</option>
                <option value="telegram">Telegram</option>
                <option value="mattermost">Mattermost</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">Direction</label>
              <select
                value={form.direction}
                onChange={e => set('direction', e.target.value as Direction)}
                className={inputCls}
              >
                <option value="in">IN — data into agents</option>
                <option value="out">OUT — push results out</option>
                <option value="both">BOTH</option>
              </select>
            </div>
          </div>

          {/* Exchange provider */}
          {isExchange && (
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">Exchange</label>
              <select
                value={form.provider || 'binance'}
                onChange={e => set('provider', e.target.value)}
                className={inputCls}
              >
                {EXCHANGE_PROVIDERS.map(p => (
                  <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>
                ))}
              </select>
              <p className="text-xs text-slate-500 mt-1">
                Uses CCXT. Public endpoints work without API keys; private (balance, orders) require credentials.
              </p>
            </div>
          )}

          {/* News provider */}
          {isNews && (
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">News Provider</label>
              <select
                value={form.provider || 'combined'}
                onChange={e => set('provider', e.target.value)}
                className={inputCls}
              >
                {NEWS_PROVIDERS.map(p => (
                  <option key={p} value={p}>
                    {p === 'combined' ? 'Combined (Alpaca + Finnhub)' : p === 'alpaca' ? 'Alpaca News' : 'Finnhub'}
                  </option>
                ))}
              </select>
              <p className="text-xs text-slate-500 mt-1">
                {form.provider === 'alpaca' && 'Symbol-specific news (earnings, analyst ratings). Uses Alpaca credentials or env vars.'}
                {form.provider === 'finnhub' && 'General market & macro news (Fed, geopolitical). Requires Finnhub API key (free at finnhub.io).'}
                {(!form.provider || form.provider === 'combined') && 'Alpaca for symbol news + Finnhub for macro/general. Best coverage.'}
              </p>
            </div>
          )}

          {/* Base URL */}
          {(isRest || isMcp || isMattermost || form.connection_type === 'websocket') && (
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">
                Base URL <span className="text-red-400">*</span>
              </label>
              <input
                value={form.base_url || ''}
                onChange={e => set('base_url', e.target.value)}
                placeholder={isMcp ? 'https://mcp.example.com' : isMattermost ? 'https://chat.privacypal.ai or full webhook URL' : 'https://api.example.com'}
                className={inputCls}
              />
              {isMcp && (
                <p className="text-xs text-slate-500 mt-1">
                  MCP server root. Results are POSTed to <code className="text-slate-400">/mcp</code> as JSON-RPC 2.0.
                </p>
              )}
            </div>
          )}

          {/* Credentials */}
          {needsCredentials && !isInternalDb && !isTelegram && (
            <div className="space-y-3">
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider flex items-center gap-1.5">
                <Key className="w-3 h-3" />
                Credentials <span className="font-normal normal-case text-slate-500">(encrypted at rest)</span>
              </p>
              <div>
                <label className="block text-xs text-slate-400 mb-1">
                  {isMcp ? 'Bearer Token' : isExchange ? 'API Key' : isNews && form.provider === 'finnhub' ? 'Finnhub API Key' : isNews && form.provider === 'alpaca' ? 'Alpaca API Key' : 'API Key / Token'}
                </label>
                <input
                  type="password"
                  value={(form as any).api_key || ''}
                  onChange={e => set('api_key' as any, e.target.value)}
                  placeholder={editing?.has_api_key ? '••••••••• (leave blank to keep existing)' : isNews && form.provider === 'finnhub' ? 'Finnhub token (free at finnhub.io)' : 'Enter API key'}
                  className={inputCls}
                  autoComplete="off"
                />
                {isNews && (!form.provider || form.provider === 'combined') && (
                  <p className="text-xs text-slate-500 mt-1">Optional — falls back to env vars (ALPACA_API_KEY, FINNHUB_API_KEY)</p>
                )}
              </div>
              {(isExchange || (isNews && form.provider === 'alpaca')) && (
                <>
                  <div>
                    <label className="block text-xs text-slate-400 mb-1">API Secret</label>
                    <input
                      type="password"
                      value={(form as any).api_secret || ''}
                      onChange={e => set('api_secret' as any, e.target.value)}
                      placeholder={editing?.has_api_key ? '••••••••• (leave blank to keep existing)' : 'Enter secret'}
                      className={inputCls}
                      autoComplete="off"
                    />
                  </div>
                  {(form.provider === 'coinbase' || form.provider === 'okx') && (
                    <div>
                      <label className="block text-xs text-slate-400 mb-1">Passphrase</label>
                      <input
                        type="password"
                        value={(form as any).api_passphrase || ''}
                        onChange={e => set('api_passphrase' as any, e.target.value)}
                        placeholder="API passphrase"
                        className={inputCls}
                        autoComplete="off"
                      />
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Telegram credentials */}
          {isTelegram && (
            <div className="space-y-3">
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider flex items-center gap-1.5">
                <Key className="w-3 h-3" />
                Telegram Credentials <span className="font-normal normal-case text-slate-500">(encrypted at rest)</span>
              </p>
              <div className="bg-sky-900/20 border border-sky-500/20 rounded-lg p-3 text-xs text-sky-300">
                Obtain <strong>API ID</strong> and <strong>API Hash</strong> from{' '}
                <span className="font-mono text-sky-200">my.telegram.org</span>. Generate a{' '}
                <strong>Session String</strong> once using{' '}
                <span className="font-mono text-sky-200">scripts/generate_telegram_session.py</span>.
              </div>
              <div>
                <label className="block text-xs text-slate-400 mb-1">API Hash <span className="text-slate-500">(stored encrypted as API Key)</span></label>
                <input
                  type="password"
                  value={(form as any).api_key || ''}
                  onChange={e => set('api_key' as any, e.target.value)}
                  placeholder={editing?.has_api_key ? '••••••••• (leave blank to keep existing)' : '32-char hex from my.telegram.org'}
                  className={inputCls}
                  autoComplete="off"
                />
              </div>
              <div>
                <label className="block text-xs text-slate-400 mb-1">Session String <span className="text-slate-500">(stored encrypted as API Secret)</span></label>
                <input
                  type="password"
                  value={(form as any).api_secret || ''}
                  onChange={e => set('api_secret' as any, e.target.value)}
                  placeholder={editing?.has_api_key ? '••••••••• (leave blank to keep existing)' : 'Telethon StringSession value'}
                  className={inputCls}
                  autoComplete="off"
                />
              </div>
            </div>
          )}

          {/* Mattermost credentials */}
          {isMattermost && (
            <div className="space-y-3">
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider flex items-center gap-1.5">
                <Key className="w-3 h-3" />
                Mattermost Credentials <span className="font-normal normal-case text-slate-500">(encrypted at rest)</span>
              </p>
              <div className="bg-indigo-900/20 border border-indigo-500/20 rounded-lg p-3 text-xs text-indigo-300">
                <strong>Option A — Incoming Webhook:</strong> Paste the full webhook URL in Base URL (e.g. <span className="font-mono text-indigo-200">https://chat.privacypal.ai/hooks/xxxx</span>). No API key needed.
                <br /><strong>Option B — Bot Token:</strong> Base URL = server URL (e.g. <span className="font-mono text-indigo-200">https://chat.privacypal.ai</span>), API Key = bot token. Add <code className="text-indigo-200">channel_id</code> in Config JSON.
              </div>
              <div>
                <label className="block text-xs text-slate-400 mb-1">API Key / Bot Token <span className="text-slate-500">(for bot mode; leave blank if using full webhook URL)</span></label>
                <input
                  type="password"
                  value={(form as any).api_key || ''}
                  onChange={e => set('api_key' as any, e.target.value)}
                  placeholder={editing?.has_api_key ? '••••••••• (leave blank to keep existing)' : 'Bot token for REST API'}
                  className={inputCls}
                  autoComplete="off"
                />
              </div>
              <p className="text-xs text-slate-500">
                Config JSON example: <code className="text-slate-400">{'{"channel_id": "xxxxxxxxxx"}'}</code> (required for bot mode)
              </p>
            </div>
          )}

          {/* Internal DB note */}
          {isInternalDb && (
            <div className="bg-slate-900/50 rounded-lg p-3 text-xs text-slate-400 border border-slate-700">
              <Database className="w-3.5 h-3.5 inline mr-1.5 text-slate-500" />
              Internal DB connections read directly from LiquidBot's trade history, performance snapshots,
              and agent statistics. No credentials required.
            </div>
          )}

          {/* Config JSON */}
          {!isInternalDb && (
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">
                Config <span className="text-slate-500 font-normal">(optional JSON)</span>
              </label>
              <textarea
                value={configText}
                onChange={e => setConfigText(e.target.value)}
                rows={4}
                placeholder={
                  isExchange
                    ? '{"symbols": ["BTC/USDT", "ETH/USDT"], "timeframe": "1h", "limit": 100}'
                    : isMcp
                    ? '{"tool_name": "record_agent_result", "health_path": "/health"}'
                    : isTelegram
                    ? '{"api_id": "12345678", "chat": "@mychannel", "limit": 20}'
                    : '{"endpoint": "/data", "auth_header": "X-API-Key"}'
                }
                className={`${inputCls} font-mono resize-none`}
              />
              {configError && <p className="text-red-400 text-xs mt-1">{configError}</p>}
              <p className="text-xs text-slate-500 mt-1">
                {isExchange && 'symbols: list of trading pairs to fetch. timeframe: 1m/5m/1h/1d. limit: number of candles.'}
                {isMcp && 'tool_name: MCP tool to call with agent results. health_path: used for Test connection.'}
                {isRest && 'endpoint: path appended to base_url for data fetch. push_endpoint: path for result push.'}
                {isTelegram && 'api_id: your Telegram App ID from my.telegram.org. chat: @username or numeric ID of the channel/group. limit: messages to fetch per run (IN). Optionally add message_template for OUT formatting.'}
              </p>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-700">
          {saveError && (
            <p className="text-red-400 text-xs flex-1 flex items-center gap-1.5">
              <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
              {saveError}
            </p>
          )}
          <button onClick={onClose} className="px-4 py-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-white text-sm">
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving || !form.name.trim()}
            className="px-4 py-2 rounded-lg bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium disabled:opacity-50 flex items-center gap-2"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            {editing ? 'Save Changes' : 'Create Connection'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

const ConnectionsPage: React.FC = () => {
  const [connections, setConnections] = useState<DataConnection[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingConn, setEditingConn] = useState<DataConnection | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<DataConnection | null>(null);
  const [testingId, setTestingId] = useState<string | null>(null);
  const [testBanner, setTestBanner] = useState<{ success: boolean; message: string } | null>(null);

  const loadConnections = async () => {
    try {
      const data = await connSvc.getConnections();
      setConnections(data);
    } catch (err) {
      console.error('Failed to load connections', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadConnections(); }, []);

  const openCreate = () => { setEditingConn(null); setSaveError(null); setShowModal(true); };
  const openEdit = (c: DataConnection) => { setEditingConn(c); setSaveError(null); setShowModal(true); };

  const handleSave = async (payload: DataConnectionCreate | DataConnectionUpdate) => {
    setSaving(true);
    setSaveError(null);
    try {
      if (editingConn) {
        await connSvc.updateConnection(editingConn.id, payload as any);
      } else {
        await connSvc.createConnection(payload as DataConnectionCreate);
      }
      setShowModal(false);
      await loadConnections();
    } catch (err: any) {
      const detail = err?.response?.data?.detail || err?.message || 'Failed to save connection';
      setSaveError(typeof detail === 'string' ? detail : JSON.stringify(detail));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await connSvc.deleteConnection(deleteTarget.id);
      setDeleteTarget(null);
      await loadConnections();
    } catch (err) {
      console.error('Delete failed', err);
    }
  };

  const handleTest = async (conn: DataConnection) => {
    setTestingId(conn.id);
    setTestBanner(null);
    try {
      const result = await connSvc.testConnection(conn.id);
      setTestBanner({ success: result.success, message: result.message });
      await loadConnections();
    } catch (err: any) {
      setTestBanner({ success: false, message: err?.message || 'Test request failed' });
    } finally {
      setTestingId(null);
    }
  };

  // Stats
  const active = connections.filter(c => c.is_active).length;
  const byType = connections.reduce<Record<string, number>>((acc, c) => {
    acc[c.connection_type] = (acc[c.connection_type] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-white mb-2">Data Connections</h1>
          <p className="text-slate-400">
            Manage exchange feeds, REST APIs, and MCP servers — attach them to agents as data IN or OUT channels
          </p>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-500 hover:bg-blue-600 text-white font-medium text-sm transition"
        >
          <Plus className="w-4 h-4" />
          New Connection
        </button>
      </div>

      {/* Stats bar */}
      {connections.length > 0 && (
        <div className="flex items-center gap-6 text-sm flex-wrap">
          <div className="text-slate-400">
            <span className="text-white font-semibold">{connections.length}</span> total
          </div>
          <div className="text-slate-400">
            <span className="text-green-400 font-semibold">{active}</span> active
          </div>
          {Object.entries(byType).map(([type, count]) => (
            <div key={type} className="text-slate-400">
              <span className="text-slate-300 font-semibold">{count}</span>{' '}
              {CONNECTION_TYPE_META[type as ConnectionType]?.label || type}
            </div>
          ))}
        </div>
      )}

      {/* Grid */}
      {loading ? (
        <div className="text-center text-slate-400 py-16">Loading connections...</div>
      ) : connections.length === 0 ? (
        <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-12 text-center">
          <Link className="w-12 h-12 text-slate-600 mx-auto mb-4" />
          <p className="text-slate-400 mb-2">No connections yet.</p>
          <p className="text-slate-500 text-sm">
            Connect an exchange, REST API, or MCP server to feed real data into your agents.
          </p>
        </div>
      ) : (
        <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))' }}>
          {connections.map(conn => (
            <ConnectionCard
              key={conn.id}
              conn={conn}
              onEdit={openEdit}
              onDelete={setDeleteTarget}
              onTest={handleTest}
              testing={testingId === conn.id}
            />
          ))}
        </div>
      )}

      {/* Modals */}
      {showModal && (
        <ConnectionModal
          editing={editingConn}
          onClose={() => setShowModal(false)}
          onSave={handleSave}
          saving={saving}
          saveError={saveError}
        />
      )}

      <ConfirmationModal
        isOpen={!!deleteTarget}
        title="Delete Connection?"
        message={`Delete "${deleteTarget?.name}"? All agent attachments to this connection will also be removed.`}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />

      {/* Test result banner */}
      {testBanner && (
        <TestBanner
          success={testBanner.success}
          message={testBanner.message}
          onClose={() => setTestBanner(null)}
        />
      )}
    </div>
  );
};

export default ConnectionsPage;
